/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDrop } from 'react-dnd'
import { batch, useDispatch, useSelector } from 'react-redux'
import { EXTERNAL_OWNER_SYSTEMS, RELATION_TYPES } from '@/modules/file/constants'

import { makeStyles } from '@mui/styles'
import IconEdit from '@assets/images/icons/pencil.svg'
import IconDelete from '@assets/images/icons/trashcan.svg'
import IconFolderNew from '@assets/images/icons/add_folder.svg'
import IconFile from '@assets/images/icons/document_blank.svg'
import IconLock from '@assets/images/icons/locked.svg'
import IconOpenFolder from '@assets/images/icons/folder_goto.svg'
import IconMenu from '@assets/images/icons/menu.svg'
import IconDesktop from '@assets/images/icons/desktop_app.svg'
import IconFavorite from '@assets/images/icons/favorite.svg'
import IconFolderFavorite from '@assets/images/icons/folder_favorite.svg'
import IconRemoveFavorite from '@assets/images/icons/no_favorite.svg'
import IconArrowRight from '@assets/images/icons/arr_right.svg'
import IconArrowDown from '@assets/images/icons/arr_down.svg'
import IconCheck from '@assets/images/icons/check_medium.svg'
import IconDownload from '@assets/images/icons/download.svg'
import IconFolderSupervised from '@assets/images/icons/folder_supervised.svg'
import FolderRoundedIcon from '@mui/icons-material/FolderRounded'

import clsx from 'clsx'

import lang from '@/lang'
import { addMoveMenuItem, hideLoader, revokeSelection, setFolder, showLoader, moveFiles } from '@/redux/ducks/document/list'
import { createFolder, fetchFolders, fetchListAction, setFolderFavorite, downloadFolderAsZip } from '@/redux/ducks/lists/requests/documentList'
import { SquareIconButton } from '@/modules/base/components/buttons'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import FileService from '@/modules/file/fileService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { ConfirmModal } from '@/modules/base/components/modal'
import TextPromptModal from '@/modules/base/components/modal/text-prompt-modal'
import CreateFolderModal from '@/modules/document/components/folder/create-folder-modal'
import Tooltip from '@mui/material/Tooltip'
import CascadingContextMenu from '@/modules/base/components/cascading-context-menu/cascading-context-menu'
import { cloneDeep, debounce } from 'lodash'
import { Folder } from '@/modules/file/components/folder-nav/types'
import ThemeInterface from '@/theme-interface'
import { FILE_LIST_FILTER, FORBIDDEN_FILENAME_CHARACTERS, PERMISSION_TYPES, errorCodes } from '@/modules/base/util/constants'
import { SYSTEM_FOLDER_NAMES } from '../../_helpers/systemFolders'
import { QUERY_TYPES } from '@/redux/ducks/lists'
import AccessModal from '@/modules/base/components/modal/access-modal'
import { canDeleteFolder, canShareFolder, canWriteFolder, getFolderAccessLevel } from '@/modules/file/permissions'

const { documents: { folder: {
  renameTitle,
  renameSubTitle,
  nameTooShortRename,
  deleteDescription,
  deleteTitle,
  nameTooShort,
  renameButton,
  create,
  createName,
  createSubTitle,
  createSubFolderTitle,
} } } = lang

const filterFolders = (folders, folderItem) => {
  const f = (folder) => {
    folder.children = folder.children.filter(({ path, name }) => {
      const p = path ? `${path}/${name}/` : `${name}/`
      const folderPath = folderItem.path ? `${folderItem.path}/${folderItem.name}/` : `${folderItem.name}/`

      return folderPath ? !p.startsWith(folderPath) : true
    })
    folder.children.forEach(f)
  }

  f(folders)
  return folders
}

interface UseStylesProps {
  folderDepth: number;
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  item: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    background: theme.white,
    padding: CssHelper.px(20, 10, 20, 0),
    height: 56,
    paddingLeft: ({ folderDepth }: UseStylesProps) => `calc(${folderDepth}*15px)`,
    '&.active': {
      background: theme.backgroundColor,
    },
  },
  itemWithSubline: {
    minHeight: 64,
    height: 'auto',
  },
  hover: {
    border: theme.border,
    borderColor: theme.primary,
  },
  folder: {
    display: 'flex',
    alignItems: 'center',
  },
  iconIsFavorite: {
    '& > svg': {
      width: '18px !important',
    },
  },
  labelWrapper: {
    cursor: 'pointer',
    flexGrow: 1,
    overflow: 'hidden',
  },
  labelWrapperWithSubline: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  label: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    maxWidth: '100%',
    '& svg': {
      margin: CssHelper.px(0, 10),
    },
  },
  labelWithSubline: {
    flexGrow: 0,
  },
  menuButton: {
    '& svg': {
      transform: 'rotateZ(90deg)',
    },
    backgroundColor: 'transparent !important',
    border: 'none',
  },
  name: {
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  content: {
    display: 'flex',
    flexGrow: 1,
    maxWidth: '100%',
    alignItems: 'center',
  },
  contentWithSubline: {
    alignItems: 'flex-start',
  },
  toggle: {
    width: 28,
    minWidth: 28,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleWithSubline: {
    paddingTop: 1,
  },
  toggleIcon: {
    cursor: 'pointer',
    '& > svg': {
      margin: 0,
      marginRight: -2,
    },
  },
  active: {
    color: theme.primary,
    '& *': {
      fill: CssHelper.important(theme.primary),
    },
  },
  iconWrapper: {
    position: 'relative',
    width: 30,
    minWidth: 30,
    flexShrink: 0,
    paddingTop: 1,
    '& > span': {
      height: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  iconWrapperWithSubline: { paddingTop: 1 },
  sharedFolderIcon: {
    margin: CssHelper.px(0, 10),
    width: 24,
    height: 24,
    display: 'block',
    flexShrink: 0,
    '&, & *': {
    },
  },
  folderRoundedIcon: {
    margin: CssHelper.px(0, 10),
    fontSize: 18,
  },
  folderRoundedIconActive: {
    color: theme.primary,
  },
  sharedFolderIconActive: {
    color: theme.primary,
    '&, & *': {
      fill: `${theme.primary} !important`,
    },
  },
  subline: {
    marginLeft: 0,
    marginTop: 2,
    fontSize: 12,
    lineHeight: 1.25,
    color: '#647190',
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  menuButtonWithSubline: {
    alignSelf: 'flex-start',
    marginTop: 0,
  },
  unseenBadge: {
    borderRadius: 999,
    background: theme.error,
    border: theme.border,
    color: theme.white,
    display: 'flex',
    position: 'absolute',
    right: -3,
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1.2,
    minWidth: 16,
    padding: '0 4px',
    fontSize: 12,
    top: -6,
    height: 16,
    whiteSpace: 'nowrap',
  },
  desktopAppIcon: {
    marginBottom: 3,
    marginLeft: 3,
  },
}))

export const isCurrentPath = (currentFolder, folder, strict = false) => {
  const currentPath = `${currentFolder.path ? `${currentFolder.path}/${currentFolder.name}` : currentFolder.name}/`
  const path = `${folder.path ? `${folder.path}/${folder.name}` : folder.name}/`

  if (strict) return currentPath === path
  return currentPath.includes(path)
}

type Props = {
  folder: Folder,
  activeFilter: keyof typeof FILE_LIST_FILTER,
  onRenameDone?: () => void,
  showToggle?: boolean,
  open?: boolean,
  depth?: number,
  onHover?: (x: any) => void,
  setOpen: (open: boolean) => void,
}

const FolderItem = (props: Props) => {
  const { folder, onRenameDone, activeFilter, showToggle, open, depth, setOpen, onHover } = props

  const folderDepth = Number.isInteger(depth)
    ? depth
    : (folder?.path?.split('/').filter(Boolean).length ?? 0)
  const icon = folder.isRoot
    ? IconFile
    : IconFolderFavorite

  const unseenCount = folder.unseenFilesCount + folder.unseenInvoicesCount + folder.unseenCancellationsCount
  const unseenBadgeLabel = unseenCount > 99 ? '+ 99' : unseenCount
  const isShareHUBFolder = folder.isSystemFolder && folder.name.startsWith(SYSTEM_FOLDER_NAMES.ROOT)
  const canWrite = canWriteFolder(folder)
  const canDelete = canDeleteFolder(folder)
  const canShare = canShareFolder(folder)
  const accessLevel = getFolderAccessLevel(folder)
  const isOwnerAccess = accessLevel === 'owner'
  const folderSharingLang = lang.folderSharing || {}
  const isSharedFolder = Boolean(folder.isShared) && !folder.isRoot && !folder.isFavorite
  const isRegularFolder = !folder.isRoot && !folder.isFavorite && !isSharedFolder
  const sharedWithLabel = typeof folderSharingLang.sharedWith === 'function'
    ? folderSharingLang.sharedWith(folder.shareCount ?? 0)
    : null
  const sharedByLabel = typeof folderSharingLang.sharedBy === 'function' && folder.sharedByName
    ? folderSharingLang.sharedBy(folder.sharedByName)
    : null
  const sharingSubline = folder.isShared
    ? isOwnerAccess ? sharedWithLabel : sharedByLabel
    : null
  const hasSharingSubline = Boolean(sharingSubline)
  const defaultFolderTooltipTitle = !isShareHUBFolder
    ? folder.folderType
      ? lang.documents.folder.tooltips[folder.folderType]
      : folder.name
    : lang.documents.shareHUBFolderDesc
  const accessTooltipTitle = accessLevel
    ? folderSharingLang.accessTooltip?.[accessLevel]
    : null
  const folderTooltipTitle = folder.isShared && accessTooltipTitle
    ? accessTooltipTitle
    : defaultFolderTooltipTitle
  const classes = useStyles({ ...props, folderDepth })
  const dispatch = useDispatch()

  const moveFolder = async(folder: Folder, folderId: string) => {
    dispatch(showLoader('movingFolder'))

    FileService.mvDir(folder.id, folderId ?? null)
      .finally(async() => {
        batch(() => {
          dispatch(fetchListAction({ queryType: QUERY_TYPES.ALL }))
          dispatch(fetchFolders())
          dispatch(hideLoader())
        })
      })
      .catch((error) => {
        let message = lang.defaultErrorMessage
        if (error.body.meta.systemFolder?.[0].code === errorCodes.NOT_ALLOWED) {
          message = lang.documents.folder.moveSystemFolderNoChildren
        } else if (error.body.meta?.name?.[0].code === errorCodes.TOO_LARGE) {
          message = lang.documents.folder.moveNameTooLong
        } else if (error.body.meta?.folderExists?.[0].code === errorCodes.NOT_ALLOWED) {
          message = lang.documents.folder.moveFolderExists
        }
        toast.showToast({ type: 'error', message })
      })
  }

  const {
    currentFolder,
    selectedItems,
    currentCollaboration,
    folders,
  } = useSelector((state: any) => state.document.list)

  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [accessModalOpen, setAccessModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [subFolderModalOpen, setSubFolderModalOpen] = useState(false)

  const active = useMemo(() => {
    return isCurrentPath(currentFolder, folder, true)
  }, [currentFolder.id, folder.id])

  const [collectedProps, drop] = useDrop({
    accept: 'FILE',
    drop: async({ file }) => {
      dispatch(showLoader('movingFiles'))
      const fileIds = selectedItems.length
        ? selectedItems.map(({ id }) => id)
        : [file.id]
      await dispatch(moveFiles(fileIds, folder.id))

      if (selectedItems.length) {
        dispatch(revokeSelection())
      }
      dispatch(fetchListAction({ queryType: QUERY_TYPES.ALL }))
      dispatch(fetchFolders())
      dispatch(hideLoader())
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  const itemRef = useRef(null)

  const openByHover = debounce(() => {
    setOpen(true)
  }, 500)

  useEffect(() => {
    if (collectedProps.isOver) {
      openByHover()
      onHover(itemRef.current)
    }
    if (!collectedProps.canDrop) {
      onHover(null)
    }
  }, [collectedProps, setOpen])

  const onSubmitName = async(name) => {
    setRenameModalOpen(false)
    if (name === folder.name) {
      return
    }

    const folderPath = (folder.folderPath || '').replace(/\/$/, '')
    const segments = folderPath ? folderPath.split('/') : []
    segments.pop() // drop current leaf name
    const realParentPath = segments.join('/')
    const newPath = realParentPath ? `${realParentPath}/${name}` : name

    await FileService.renameDir(folder.id, newPath, activeFilter)
    toast.showToast({ type: 'success', message: lang.documents.folder.renameSuccess })
    if (onRenameDone) {
      onRenameDone()
    }
  }

  const deleteFolder = () => {
    setDeleteModalOpen(false)
    FileService.rmDir(folder.id)
      .then(() => {
        toast.showToast({ type: 'success', message: lang.documents.folder.deleteSuccess })
      })
      .catch((error) => {
        const notAllowedError = error.meta?.['permission-delete']?.find(({ code }) => code === errorCodes.NOT_ALLOWED)
        if (notAllowedError) {
          toast.showToast({ type: 'error', message: lang.documents.folder.permissions[PERMISSION_TYPES.DELETE]?.folder })
        } else {
          toast.showToast({ type: 'error', message: lang.documents.folder.deleteFailed })
        }
      })
  }

  const onSelectFolder = (e) => {
    e.stopPropagation()
    setFolder(folder)
  }

  const onToggle = (e) => {
    e.stopPropagation()
    setOpen(!open)
  }

  const onRename = () => {
    setRenameModalOpen(true)
  }

  const onSetAccess = () => {
    setAccessModalOpen(true)
  }

  const handleModalClose = () => {
    if (renameModalOpen) {
      setRenameModalOpen(false)
    }
    if (accessModalOpen) {
      setAccessModalOpen(false)
    }

    if (subFolderModalOpen) {
      setSubFolderModalOpen(false)
    }
  }

  const onDelete = () => {
    setDeleteModalOpen(true)
  }

  const [menuPosition, setMenuPosition] = useState(null)
  const handleClick = (event) => {
    event.stopPropagation()
    setMenuPosition({
      top: event.pageY,
      left: event.pageX,
    })
  }

  const favoriteLabel = folder.isFavorite
    ? lang.documents.removeFavorite
    : lang.documents.setFavorite
  const favoriteIcon = folder.isFavorite
    ? IconRemoveFavorite
    : IconFavorite

  const onToggleFavorite = () => {
    dispatch(setFolderFavorite([folder], !folder.isFavorite))
  }

  const iconWidth = folder.isFavorite ? '21px' : '16px'

  const markAsSeen = () => {
    const path = folder.path ? `${folder.path}/${folder.name}` : folder.name
    FileService.markAsSeen(path).catch(() => {
      toast.showToast({ type: 'error', message: lang.documents.folder.markAsSeenFailed })
    })
  }

  const onCreateSubFolder = (value, accessList = []) => {
    if (!canWrite) {
      toast.showToast({ type: 'error', message: lang.documents.folder.permissions[PERMISSION_TYPES.CREATE]?.folder })
      return
    }
    createFolder(value, folder, activeFilter, folders, undefined, accessList)
    setSubFolderModalOpen(false)
  }

  const onFolderDownload = async() => {
    await dispatch(downloadFolderAsZip(folder.folderPath))
  }

  const menuItems = useMemo(() => {
    const markAsSeenItem = { label: lang.generics.markAsSeen, icon: IconCheck, onClick: markAsSeen }
    if (folder.isRoot) {
      return unseenCount ? [markAsSeenItem] : []
    }

    const menuItems = [
      { label: lang.documents.folder.openFolder, icon: IconOpenFolder, onClick: onSelectFolder },
    ]

    menuItems.push({
      label: lang.generics.downloadFolder,
      icon: IconDownload,
      disabled: folder.count === 0,
      onClick: () => {
        onFolderDownload()
      },
    })

    if (canShare && !currentCollaboration) {
      menuItems.push({ label: lang.documents.folder.openAccess, icon: IconLock, onClick: onSetAccess })
    }
    if (!folder.isSystemFolder && folder.externalOwnerSystem !== EXTERNAL_OWNER_SYSTEMS.desktop) {
      menuItems.push({
        label: lang.documents.folder.rename,
        icon: IconEdit,
        onClick: onRename,
        disabled: !canWrite,
        tooltip: !canWrite ? lang.documents.folder.permissions[PERMISSION_TYPES.UPDATE]?.folder : undefined,
      })
      const options = { includesRoot: true, callback: (id) => moveFolder(folder, id), allowSystemFolders: false }
      if ((folders.children.filter(({ isSystemFolder }) => !isSystemFolder).length > 1 || folder.path) && canWrite) {
        addMoveMenuItem(menuItems, [{ id: folder.id }], filterFolders(cloneDeep(folders), folder), options)
      }
      menuItems.push({
        label: lang.documents.folder.createSubFolderTitle,
        icon: IconFolderNew,
        onClick: () => setSubFolderModalOpen(true),
        disabled: !canWrite,
        tooltip: !canWrite ? lang.documents.folder.permissions[PERMISSION_TYPES.CREATE]?.folder : undefined,
      })
      if (!currentCollaboration) menuItems.push({ label: favoriteLabel, icon: favoriteIcon, onClick: onToggleFavorite })
      if (!currentCollaboration) {
        menuItems.push({
          label: lang.documents.folder.delete,
          icon: IconDelete,
          onClick: onDelete,
          disabled: !canDelete,
          tooltip: !canDelete ? lang.documents.folder.permissions[PERMISSION_TYPES.DELETE]?.folder : undefined,
        })
      }
    }

    if (unseenCount) menuItems.push(markAsSeenItem)

    return menuItems
  }, [folder])

  return (
    <div ref={drop} className={clsx(classes.item, { [classes.itemWithSubline]: hasSharingSubline }, collectedProps?.isOver && classes.hover, { active })}>
      <CascadingContextMenu menuItems={menuItems} position={menuPosition} onClose={() => setMenuPosition(null)}>
        <span ref={itemRef} className={clsx(classes.folder)} onClick={onSelectFolder} >
          <div className={clsx(classes.content, { [classes.contentWithSubline]: hasSharingSubline })}>
            <div className={clsx(classes.toggle, { [classes.toggleWithSubline]: hasSharingSubline })}>
              {showToggle && folder.children.length
                ? <SVGIcon
                  id='folderItemExpandIcon'
                  className={classes.toggleIcon}
                  svg={open ? IconArrowDown : IconArrowRight}
                  width={'16px'}
                  height={'16px'}
                  onClick={onToggle} />
                : null
              }
            </div>
            <div className={clsx(classes.iconWrapper, { [classes.iconWrapperWithSubline]: hasSharingSubline })}>
              <span>
                {isSharedFolder
                  ? <SVGIcon
                    id='sharedFolderItemIcon'
                    className={clsx(classes.sharedFolderIcon, { [classes.sharedFolderIconActive]: active })}
                    svg={IconFolderSupervised}
                    width='24px'
                    height='24px' />
                  : isRegularFolder
                    ? <FolderRoundedIcon className={clsx(classes.folderRoundedIcon, { [classes.folderRoundedIconActive]: active })} />
                  : <SVGIcon id='folderItemIcon' className={clsx({ [classes.iconIsFavorite]: folder.isFavorite, [classes.active]: active })}
                    svg={icon}
                    width={iconWidth}
                    height={'16px'} />}
                {unseenCount > 0 ? <span className={classes.unseenBadge}><span>{unseenBadgeLabel}</span></span> : null}
              </span>
            </div>
            <div className={clsx(classes.labelWrapper, { [classes.labelWrapperWithSubline]: hasSharingSubline })}>
              <span className={clsx(classes.label, { [classes.labelWithSubline]: hasSharingSubline })}>
                <Tooltip
                  enterDelay={folder.isShared ? 500 : 3000}
                  title={folderTooltipTitle}>
                  <span className={clsx(classes.name, { [classes.active]: active })}>{folder.name}</span>
                </Tooltip>
                {folder.externalOwnerSystem === EXTERNAL_OWNER_SYSTEMS.desktop
                  ? <SVGIcon id='desktopAppIcon' className={clsx(classes.desktopAppIcon)}
                    svg={IconDesktop}
                    width={iconWidth}
                    height={'18px'} />
                  : null}
              </span>
              {sharingSubline ? <span className={classes.subline}>{sharingSubline}</span> : null}
            </div>
            {folder.isRoot
              ? null
              : <SquareIconButton
                icon={IconMenu}
                className={clsx(classes.menuButton, { [classes.menuButtonWithSubline]: hasSharingSubline })}
                onClick={handleClick}
              />}
          </div>
        </span>
        {deleteModalOpen && <ConfirmModal
          open={true}
          title={deleteTitle}
          description={deleteDescription}
          onConfirm={deleteFolder}
          onAbort={() => setDeleteModalOpen(false)} />}
      </CascadingContextMenu>
      {renameModalOpen && <TextPromptModal
        invalidCharacters={FORBIDDEN_FILENAME_CHARACTERS}
        open
        value={folder.name}
        onSubmit={onSubmitName}
        onClose={handleModalClose}
        title={renameTitle}
        subTitle={renameSubTitle}
        emptyErrorMessage={nameTooShortRename}
        confirmButtonLabel={renameButton} />}
      {subFolderModalOpen && <CreateFolderModal
        value={createName}
        invalidCharacters={FORBIDDEN_FILENAME_CHARACTERS}
        open
        onSubmit={onCreateSubFolder}
        onClose={handleModalClose}
        title={createSubFolderTitle}
        subTitle={createSubTitle}
        emptyErrorMessage={nameTooShort}
        confirmButtonLabel={create} />}
      {accessModalOpen && <AccessModal
        type={RELATION_TYPES.document}
        folderName={folder.name ?? ''}

        id={folder.id}
        open
        onClose={handleModalClose}
      />}
    </div>
  )
}

FolderItem.propTypes = {
  folder: PropTypes.object,
  onRename: PropTypes.func,
  activeFilter: PropTypes.string,
  setOpen: PropTypes.func.isRequired,
}

export default FolderItem
